import React, { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { View } from "@design-system/core"
import { __DEV__ } from "@design-system/utils"
import { PortalProps, OnLayerHolderRef, PortalLayer } from "./types"
import { PortalContextProvider } from "./PortalContext"
import { addOrUpdateLayer, removeLayer } from "./layers"
import { renderPortalHost } from "./RenderPortal"

// init portal host
renderPortalHost()

let globalPortalId = 1

const getNextId = () => globalPortalId++

export function Portal(props: PortalProps) {
  const {
    withPassthroughEvents,
    children,
    zIndex,
    order,
    positionFixed,
    topPositioned,
    bottomPositioned,
  } = props
  const isMounted = useRef(false)
  const [domRef, setDomRef] = useState<HTMLElement | null>()
  const idRef = useRef(getNextId())

  const onLayerHolderRef: OnLayerHolderRef = useCallback(
    (ref: HTMLElement | null) => {
      if (domRef !== ref && isMounted.current) {
        setDomRef(ref)
      }
    },
    [domRef],
  )

  const stopEventHandler = useCallback(
    (e: Event) => {
      e.stopPropagation()
    },
    [withPassthroughEvents],
  )

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    const layer: PortalLayer = {
      id: idRef.current,
      zIndex: zIndex ?? 1001,
      order: order ?? 0,
      fixed: positionFixed ?? false,
      topPositioned: topPositioned ?? false,
      bottomPositioned: bottomPositioned ?? false,
      onRef: onLayerHolderRef,
    }

    addOrUpdateLayer(layer)
  }, [zIndex, order, positionFixed])

  useEffect(() => () => removeLayer(idRef.current), [])

  const eventHandler: any = withPassthroughEvents ? undefined : stopEventHandler

  if (!domRef) {
    return null
  }

  return createPortal(
    <View
      onBlur={eventHandler}
      onChange={eventHandler}
      onClick={eventHandler}
      onContextMenu={eventHandler}
      onFocus={eventHandler}
      onKeyDown={eventHandler}
      onKeyPress={eventHandler}
      onKeyUp={eventHandler}
      onMouseDown={eventHandler}
      onMouseEnter={eventHandler}
      onMouseLeave={eventHandler}
      onMouseMove={eventHandler}
      onMouseOut={eventHandler}
      onMouseOver={eventHandler}
      onMouseUp={eventHandler}
      onWheel={eventHandler}
      pointerEvents="box-none"
    >
      <PortalContextProvider value={{ layerId: idRef.current }}>
        {children}
      </PortalContextProvider>
    </View>,
    domRef,
  )
}

if (__DEV__) {
  Portal.displayName = "Portal"
}
